/** Per-lane minimize state for the video editor timeline (label column toggles). */

/** Height of a minimized lane — clip bars stay visible as thin slivers. */
export const COLLAPSED_TIMELINE_LANE_HEIGHT = 22;

export function timelineLaneKey(kind, trackIdx = 0) {
  return `${String(kind || 'lane')}:${Number(trackIdx) || 0}`;
}

export function isTimelineLaneCollapsed(collapsed, key) {
  return Boolean(collapsed && collapsed[key]);
}

export function toggleTimelineLaneCollapsed(collapsed, key) {
  const prev = collapsed && typeof collapsed === 'object' ? collapsed : {};
  if (!key) return prev;
  if (prev[key]) {
    const next = { ...prev };
    delete next[key];
    return next;
  }
  return { ...prev, [key]: true };
}

/** Collapse state with every given lane minimized, keeping unknown lanes untouched. */
export function collapseTimelineLanes(collapsed, keys) {
  const prev = collapsed && typeof collapsed === 'object' ? collapsed : {};
  const next = { ...prev };
  (Array.isArray(keys) ? keys : []).forEach((key) => {
    if (key) next[key] = true;
  });
  return next;
}

/** Collapse state with every given lane expanded. */
export function expandTimelineLanes(collapsed, keys) {
  const prev = collapsed && typeof collapsed === 'object' ? collapsed : {};
  const next = { ...prev };
  (Array.isArray(keys) ? keys : []).forEach((key) => {
    delete next[key];
  });
  return next;
}

export function areAllTimelineLanesCollapsed(collapsed, keys) {
  const list = (Array.isArray(keys) ? keys : []).filter(Boolean);
  if (!list.length) return false;
  return list.every((key) => isTimelineLaneCollapsed(collapsed, key));
}

/** One click swaps between all-minimized and all-expanded. */
export function toggleAllTimelineLanes(collapsed, keys) {
  return areAllTimelineLanesCollapsed(collapsed, keys)
    ? expandTimelineLanes(collapsed, keys)
    : collapseTimelineLanes(collapsed, keys);
}

/** Row sizing for a lane: fixed short height when minimized, otherwise its normal min height. */
export function timelineLaneRowSizeStyle(collapsed, expandedMinHeight) {
  if (!collapsed) return { minHeight: expandedMinHeight };
  return {
    minHeight: COLLAPSED_TIMELINE_LANE_HEIGHT,
    height: COLLAPSED_TIMELINE_LANE_HEIGHT,
  };
}

/**
 * Clips a minimized lane's clip area so squashed clip bars can't bleed into neighbours.
 * Must stay off the lane row itself — an `overflow: hidden` ancestor would become the
 * scroll container for the sticky label column and stop it from sticking while scrolling.
 */
export function timelineLaneClipAreaStyle(collapsed) {
  return collapsed ? { overflow: 'hidden' } : null;
}
